import { byZkxEnv } from '../utils/zkxEnv'

// ZKX deployments of the open-source Ambire contracts (EIP-2470, salt
// 0x1913af83…d2e0 → same address on every chain we deploy to). Deployed on
// chains 1 / 56 / 8453 / 369 — see zkx-wallet-pro docs/transactions.md.
// ⚠️ These consts are GLOBAL: chains where we have NOT deployed must be gated
// off in consts/networks.ts (areContractsDeployed: false), or users could fund
// an SA address that can never be deployed there.
//
// There are TWO deployed sets, and the build picks one by BACKEND, not by chain:
// the staging set is what apis.zkxteam.xyz's relayer indexes and what every
// account created on a dev build already lives under; the production set was
// deployed fresh for release (docs/gas-tank-prod-contracts.md).
//
// Every table below is keyed by `ENV_MODE` through `byZkxEnv`, spelling out all
// three environments rather than branching on a boolean. `uat` is production in
// every respect — `ZKX_API_BASE` in utils/networks.ts already sends it to
// apis.zkxwallet.com — so it maps to the PRODUCTION contracts, and writing that
// out makes it a recorded decision instead of whichever side of a `? :` it
// happened to fall on. Key every table in this file the same way as that host
// table.
const STAGING_PROXY_AMBIRE_ACCOUNT = '0x023F41F88686652dF954be2a3001955cf9342bf4'
const PROD_PROXY_AMBIRE_ACCOUNT = '0x0FE32a0323223aBEB91cAb533Eed1f317458F6B4'
export const PROXY_AMBIRE_ACCOUNT = byZkxEnv({
  dev: STAGING_PROXY_AMBIRE_ACCOUNT,
  uat: PROD_PROXY_AMBIRE_ACCOUNT,
  prod: PROD_PROXY_AMBIRE_ACCOUNT
})

// ⚠️ The factory is the account's IDENTITY: the SA address is CREATE2-derived
// from it (`getAmbireAccountAddress`), and `isAmbireV2Account` /
// `deprecatedSmartAccountBanner` compare a stored `creation.factoryAddr` against
// it. Accounts already created keep their own `creation` record, so they keep
// working and stay signable — but an account created on a DEV build and imported
// into a prod build (or the reverse) derives a different address and shows the
// "old account" banner. That is acceptable only because both ZKX-native account
// features shipped OFF (SMART_ACCOUNT_ENABLED / GAS_TANK_ENABLED, see
// web/constants/features.ts): no release build has ever created an SA under the
// staging factory. Do NOT change either address again once a release has.
const STAGING_AMBIRE_ACCOUNT_FACTORY = '0xdEfb15f39Ac7Db91D1fa7815E4077B75f343addC'
const PROD_AMBIRE_ACCOUNT_FACTORY = '0x4AF39924165f02bAAED8D57Ae1d08286FEA35990'
export const AMBIRE_ACCOUNT_FACTORY = byZkxEnv({
  dev: STAGING_AMBIRE_ACCOUNT_FACTORY,
  uat: PROD_AMBIRE_ACCOUNT_FACTORY,
  prod: PROD_AMBIRE_ACCOUNT_FACTORY
})

// The ONLY chains where the ZKX factory/proxy above are actually deployed.
// This is the single source of truth for "may a Smart Account exist here?".
// Funds-safety allowlist: the relayer-config merge
// (`mapRelayerNetworkConfigToAmbireNetwork` in utils/networks.ts) and any other
// path that trusts the backend to enable Smart Accounts MUST intersect against
// this set, so a misconfigured backend advertising `smartAccounts` for a chain
// we never deployed to cannot arm SA there (→ stuck funds). Extend this list
// (and log the deploy txs in docs/transactions.md) only when the factory is
// actually deployed to the same address on a new chain via EIP-2470.
export const ZKX_SA_DEPLOYED_CHAIN_IDS: bigint[] = [1n, 56n, 8453n, 369n]
// The ERC-4337 paymaster the wallet asks to sponsor a userOp, via the relayer's
// `/v2/paymaster/:chainId/request` route. Unlike the factory above, an address
// being DEPLOYED is not enough: the relayer looks it up in its OWN registry and
// answers `Paymaster not found` for anything else, and the sponsorship it signs
// is only valid if its key matches that paymaster's `relayer` immutable. A
// mismatch makes every transaction that reaches the bundler (fee paid in an
// ERC-20, or zero native balance — see `EOA7702.getBroadcastOption`)
// unbroadcastable. The Gas Tank / Smart Account kill-switches do not protect
// against it: they gate UI surfaces, not this file.
//
// Three deployments exist, all verified on 1 / 56 / 8453 with `relayer()` read
// on-chain:
//   • staging ZKX  0x30DC…44F5   signer 0xD70994…3868 — what apis.dev signs for.
//   • production   0x364e98…322d signer 0xf5404d…3869 — the fresh release set.
//   • Ambire       0xA8B267…2Cf0 signer 0x39f1B4…27f3 — upstream's own.
//
// ⚠️ As probed on 2026-08-26, apis.zkxwallet.com (prod AND uat) still PROXIES to
// relayer.ambire.com, whose registry knows ONLY Ambire's paymaster: BOTH ZKX
// addresses answer `Paymaster not found` there. The flag below is set to `true`
// anyway, on the explicit call that the config should already name ZKX's own
// production deployment and the backend follows — so until that backend work
// lands, a prod/uat build cannot broadcast through the bundler.
//
// That is the single symptom to look for: `Paymaster not found` on a release
// build. Setting this to `false` falls back to Ambire's paymaster, which the
// proxied relayer does sign for, and is the one-line mitigation if a release has
// to ship before the backend registers 0x364e98…3869.
const PROD_RELAYER_KNOWS_ZKX_PAYMASTER = true

const STAGING_PAYMASTER = {
  address: '0x30DC602E16B957B333AD1369FD115282D4Df44F5',
  signer: '0xD70994e8d6D4e70cBc4259acAfc2e13EA8D83868'
}
const PROD_ZKX_PAYMASTER = {
  address: '0x364e984C82ea307020ca8F605B10DDc24f32322d',
  signer: '0xf5404d690EC0CcCAcC85D082412A9C2fBD383869'
}
const AMBIRE_UPSTREAM_PAYMASTER = {
  address: '0xA8B267C68715FA1Dca055993149f30217B572Cf0',
  signer: '0x39f1B4be409F2DFC8FCB84C1196537728df727f3'
}

const PRODUCTION_PAYMASTER = PROD_RELAYER_KNOWS_ZKX_PAYMASTER
  ? PROD_ZKX_PAYMASTER
  : AMBIRE_UPSTREAM_PAYMASTER

const PAYMASTER = byZkxEnv({
  dev: STAGING_PAYMASTER,
  uat: PRODUCTION_PAYMASTER,
  prod: PRODUCTION_PAYMASTER
})

export const AMBIRE_PAYMASTER = PAYMASTER.address
export const AMBIRE_PAYMASTER_SIGNER = PAYMASTER.signer
export const ERC_4337_ENTRYPOINT = '0x0000000071727De22E5E9d8BAf0edAc6f37da032'
export const ENTRY_POINT_MARKER =
  '0x0000000000000000000000000000000000000000000000000000000000007171'
export const MAX_UINT256 =
  115792089237316195423570985008687907853269984665640564039457584007913129639935n
export const MAX_UINT160 = 1461501637330902918203684832716283019655932542975n

export const PROXY_AMBIRE_4337_ACCOUNT = '0xd590a2aBA89a590b15De795DE559e7166aC293eA'
export const SINGLETON = '0xce0042B868300000d44A59004Da54A005ffdcf9f'
export const OPTIMISTIC_ORACLE = '0x420000000000000000000000000000000000000F'
export const SCROLL_ORACLE = '0x5300000000000000000000000000000000000002'
export const DEPLOYLESS_SIMULATION_FROM = '0x0000000000000000000000000000000000000001'

// ZKX AmbireAccount7702 delegation target — deployed on 1 / 56 / 8453 only
// (PulseChain 369 has no EIP-7702; it uses the v2 smart-account path instead).
// Same staging/production split as the factory, same `byZkxEnv` table.
// The staging address stays exported because a delegation is ON-CHAIN state: an
// EOA delegated by a dev build points at it forever, and dropping it from the
// recognition list (consts/7702.ts) would make the wallet read that account as a
// foreign delegation it cannot revoke. Recognition only — never a NEW target.
export const EIP_7702_AMBIRE_ZKX_STAGING = '0xDd3bA5ED138479E28A2351B8e1201689859c68aE'
const PROD_EIP_7702_AMBIRE_ACCOUNT = '0xb2fe786eC2584BAC0217251Cabc485e0B1116A9E'
export const EIP_7702_AMBIRE_ACCOUNT = byZkxEnv({
  dev: EIP_7702_AMBIRE_ZKX_STAGING,
  uat: PROD_EIP_7702_AMBIRE_ACCOUNT,
  prod: PROD_EIP_7702_AMBIRE_ACCOUNT
})

// The ONLY chains where EIP_7702_AMBIRE_ACCOUNT above is actually deployed.
// The relayer config advertises `has7702: true` on ~25 chains (correct — they
// all support EIP-7702), but that flag says nothing about OUR delegate being
// there. Gate NEW delegations (`canBecomeSmarterOnChain`) on this list so a
// user cannot delegate to an address with no code on that chain. Note this is
// NOT the same set as ZKX_SA_DEPLOYED_CHAIN_IDS: 369 has the v2 factory but no
// EIP-7702 support at all.
export const ZKX_7702_DEPLOYED_CHAIN_IDS: bigint[] = [1n, 56n, 8453n]

// Upstream Ambire's AmbireAccount7702, the delegation target ZKX shipped with
// before the constant above. Accounts delegated by an older build still point
// here on-chain, so it must stay RECOGNIZED (consts/7702.ts) — otherwise the
// wallet reads them as a foreign delegation and the Smart Settings sheet cannot
// revoke them. Recognition only: never a target for a NEW delegation. It is the
// post-Feb-2025 contract, so it also verifies userOps with a DIFFERENT EIP-712
// scheme than ours — see the isHotEOA branch in signAccountOp.ts.
export const EIP_7702_AMBIRE_LEGACY = '0x5A7FC11397E9a8AD41BF10bf13F22B0a63f96f6d'
export const EIP_7702_METAMASK = '0x63c0c19a282a1B52b07dD5a65b58948A07DAE32B'
export const EIP_7702_GRID_PLUS = '0x8D6220c9961E8DD1844108C854F514E120226E20'
export const EIP_7702_KATANA = '0x8226995E02C70293595E0634C5F89547EDb08126'
